// File Metadata:
// Filename: Navbar.jsx
// Version: 3.00
// Created: 2025-03-19 23:09:16
// Last Modified: 2026-06-14
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: "Obsidian Home" top navigation bar — glass, teal brand, clean user menu

import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AppBar, Toolbar, IconButton, Box, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import DevicesIcon from '@mui/icons-material/Devices';
import { Link } from 'react-router-dom';
import { useTheme as useAppTheme } from '@/contexts/ThemeContext';
import { selectTimerStatus, selectTimerRemaining } from '@/features/Timer/timerSlice';
import { alpha, useTheme } from '@mui/material/styles';
import TimerIndicator from '@/components/TimerIndicator';
import ThemeToggle from '@/components/ThemeToggle';
import UserAvatar from '@/components/UserAvatar';
import UserMenu from '@/components/UserMenu';
import LoginButton from '@/components/LoginButton';

const brandFont = '"Bricolage Grotesque", "Helvetica Neue", sans-serif';

const Navbar = ({ toggleDrawer }) => {
  const user = useSelector((state) => state.user.data);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { mode, toggleTheme } = useAppTheme();
  const timerStatus = useSelector(selectTimerStatus);
  const timerRemaining = useSelector(selectTimerRemaining);

  // Format remaining time for display — rounded to integer seconds, fixed-width MM:SS format
  const formattedTime = useMemo(() => {
    if (timerStatus !== 'running' && timerStatus !== 'paused') return null;
    const totalSeconds = Math.ceil(timerRemaining); // Round up to nearest second
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [timerStatus, timerRemaining]);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const themeLabel = mode === 'light' ? 'Light'
                   : mode === 'dark'  ? 'Dark'
                   : 'System';

  return (
    <AppBar position="fixed" elevation={0} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
      <Toolbar sx={{ gap: 1, minHeight: { xs: 56, sm: 64 } }}>
        {/* Mobile hamburger */}
        <IconButton
          edge="start"
          color="inherit"
          onClick={toggleDrawer}
          sx={{ mr: 1, display: { md: 'none' } }}
          aria-label="Open navigation"
        >
          <MenuIcon />
        </IconButton>

        {/* Brand */}
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            textDecoration: 'none',
            color: 'inherit',
            flexGrow: 1,
          }}
        >
          {/* Logo mark */}
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark
              ? 'linear-gradient(145deg, #2dd4bf 0%, #0d9488 100%)'
              : 'linear-gradient(145deg, #0d9488 0%, #0f766e 100%)',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
            flexShrink: 0,
          }}>
            <Typography sx={{
              fontFamily: brandFont,
              fontWeight: 800,
              fontSize: '0.85rem',
              color: isDark ? '#0f172a' : '#ffffff',
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}>
              FS
            </Typography>
          </Box>

          <Typography variant="h6" sx={{
            fontFamily: brandFont,
            fontWeight: 700,
            fontSize: '1.1rem',
            letterSpacing: '-0.02em',
            background: isDark
              ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
              : 'none',
            WebkitBackgroundClip: isDark ? 'text' : 'unset',
            WebkitTextFillColor: isDark ? 'transparent' : 'inherit',
            display: { xs: 'none', sm: 'block' },
          }}>
            FamilySync
          </Typography>
        </Box>

        {user ? (
          <>
            {/* Timer indicator — shown when timer is running/paused */}
            {formattedTime && <TimerIndicator isDark={isDark} formattedTime={formattedTime} />}

            <ThemeToggle mode={mode} toggleTheme={toggleTheme} themeLabel={themeLabel} />
            <UserAvatar user={user} onMenuOpen={handleMenuOpen} />
            <UserMenu user={user} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} />
          </>
        ) : (
          <LoginButton />
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
